/**
 * A navigation card the user created for themselves. The blocks are stored per user in the custom
 * fields of the ICM user profile, see ACCOUNT_NAVIGATION_BLOCKS_FIELD, and are rendered in the account
 * card navigation below the items of the group they were added to.
 */
export interface AccountNavigationBlock {
  /** stable id, generated when the block is created */
  id: string;
  /** id of the navigation group the block was added to */
  groupId: string;
  /** the label of the card */
  name: string;
  /** optional second line of the card */
  description?: string;
  /** bootstrap icon class, one of ACCOUNT_NAVIGATION_BLOCK_ICONS or a custom one the user named */
  icon: string;
  /** an internal route starting with a slash or an absolute http(s) url */
  url: string;
  /** true if the url leaves the storefront and has to be opened as a plain link */
  external: boolean;
}

/** one of the sample logos the user picks from, with the label that names it for screen readers */
export interface AccountNavigationBlockIcon {
  /** bootstrap icon class */
  icon: string;
  /** localization key of the name of the logo */
  localizationKey: string;
}

/**
 * The sample logos offered when a block is created. Bootstrap icons, so a block looks like the cards
 * of the standard navigation instead of introducing an own icon set. An icon carries a label because
 * it is the only content of its option in the picker and needs an accessible name of its own.
 */
export const ACCOUNT_NAVIGATION_BLOCK_ICONS: AccountNavigationBlockIcon[] = [
  { icon: 'bi-link-45deg', localizationKey: 'account.navigation.block.icon.link' },
  { icon: 'bi-star', localizationKey: 'account.navigation.block.icon.star' },
  { icon: 'bi-bookmark', localizationKey: 'account.navigation.block.icon.bookmark' },
  { icon: 'bi-box-seam', localizationKey: 'account.navigation.block.icon.package' },
  { icon: 'bi-truck', localizationKey: 'account.navigation.block.icon.delivery' },
  { icon: 'bi-headset', localizationKey: 'account.navigation.block.icon.support' },
  { icon: 'bi-file-earmark-text', localizationKey: 'account.navigation.block.icon.document' },
  { icon: 'bi-graph-up', localizationKey: 'account.navigation.block.icon.report' },
  { icon: 'bi-calendar-event', localizationKey: 'account.navigation.block.icon.calendar' },
  { icon: 'bi-people', localizationKey: 'account.navigation.block.icon.team' },
  { icon: 'bi-gear', localizationKey: 'account.navigation.block.icon.settings' },
  { icon: 'bi-globe', localizationKey: 'account.navigation.block.icon.website' },
];

/** the icon a block falls back to when it carries none or an unknown one */
export const ACCOUNT_NAVIGATION_BLOCK_DEFAULT_ICON = ACCOUNT_NAVIGATION_BLOCK_ICONS[0].icon;

/**
 * The value the logo picker carries while the user names an icon of their own instead of picking one
 * of the samples. It is a state of the form only, never a stored icon: what is stored is the icon the
 * user named.
 */
export const ACCOUNT_NAVIGATION_BLOCK_CUSTOM_LOGO = 'custom';

/** the lengths the form accepts, kept next to the model so the mapper can cut a tampered value */
export const ACCOUNT_NAVIGATION_BLOCK_NAME_MAX_LENGTH = 35;
export const ACCOUNT_NAVIGATION_BLOCK_DESCRIPTION_MAX_LENGTH = 120;
